/*
 * The Vendor Logs screen's state.
 *
 * `capture.mode` is the part that matters. `full` records the raw provider response, which can
 * carry a real customer's name and bank account, so the server turns it off by itself after a
 * window and deletes what it captured. The screen's job is to make that expiry visible while it is
 * running rather than after — a capture mode nobody remembers enabling is the way a debugging
 * convenience becomes a data-retention incident.
 *
 * A body is fetched only when someone opens one. Listing does not carry bodies, and this keeps it
 * that way: the expensive, sensitive half stays behind a deliberate click.
 */
import { useCallback, useEffect, useState } from 'react';
import { ApiError, superadminApi } from '../../../api';
import type { VendorCaptureSettingsDto, VendorExchangeDto } from '../../../api';

export interface VendorLogsState {
  items: VendorExchangeDto[];
  capture: VendorCaptureSettingsDto | null;
  loading: boolean;
  loadError: string;
  error: string;
  vendorFilter: string;
  setVendorFilter: (v: string) => void;
  errorsOnly: boolean;
  setErrorsOnly: (v: boolean) => void;
  openId: string | null;
  open: (id: string | null) => void;
  body: { body: string | null; captureMode: string; bodyTruncated: boolean } | null;
  bodyLoading: boolean;
  setCaptureMode: (mode: VendorCaptureSettingsDto['mode']) => Promise<void>;
  reload: () => void;
}

export function useVendorLogs(token: string): VendorLogsState {
  const [items, setItems] = useState<VendorExchangeDto[]>([]);
  const [capture, setCapture] = useState<VendorCaptureSettingsDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [error, setError] = useState('');
  const [vendorFilter, setVendorFilter] = useState('all');
  const [errorsOnly, setErrorsOnly] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [body, setBody] = useState<VendorLogsState['body']>(null);
  const [bodyLoading, setBodyLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    superadminApi.listVendorExchanges(token)
      .then((res) => { setItems(res.items); setCapture(res.capture); setLoadError(''); })
      .catch(() => setLoadError('โหลดข้อมูลการตอบกลับจากผู้ให้บริการไม่สำเร็จ'))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { load(); }, [load]);

  return {
    items, capture, loading, loadError, error,
    vendorFilter, setVendorFilter, errorsOnly, setErrorsOnly,
    openId, body, bodyLoading,
    open: (id) => {
      setOpenId(id);
      setBody(null);
      if (!id) return;
      const row = items.find((i) => i.id === id);
      if (!row?.hasBody) return;
      setBodyLoading(true);
      superadminApi.revealVendorExchange(token, id)
        .then(setBody)
        .catch(() => setBody(null))
        .finally(() => setBodyLoading(false));
    },
    setCaptureMode: async (mode) => {
      setError('');
      try {
        const res = await superadminApi.setVendorCapture(token, mode);
        setCapture(res.capture);
      } catch (e) {
        setError(e instanceof ApiError ? e.message : 'เปลี่ยนโหมดการเก็บข้อมูลไม่สำเร็จ');
      }
    },
    reload: load,
  };
}
